import { IconButton } from "../../../components/button/Button";
import SearchBar from "../../../components/searchBar/SearchBar";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Icon } from "../../../components/icon/Icon";
import InlineEditor from "../../../components/inlineEditor/InlineEditor";
import { softContains } from "../../../utils/stringUtil";
import { isEmpty, keyBy, uniq } from "lodash-es";
import SubformService from "../../../services/SubformService";
import type { Subform } from "../../../state/formVersion";
import { MAX_NUMBER_SM } from "../../../constants";
import type { TranslationKey } from "../../../state/i18n";
import { useTranslation } from "../../../state/i18n";
import { MenuPill } from "../../../components/pill/InteractivePill";
import { DestructiveModal } from "../../../components/modal/Modal";
import NavigationService from "../../../services/NavigationService";
import type { SubformFieldProperties } from "../../../fields/subform/SubformField";
import type { PinFieldProperties } from "../../../fields/media/PinField";
import { NothingToRender } from "../../../components/NothingToRender";
import SubformShowEvent from "../../../events/SubformShowEvent";
import SubformCreateModal from "./SubformCreateModal";
import type { FieldConfig } from "../../../fields/types";
import { IconButtonMenu } from "../../../components/listItemMenu/ListItemMenu";
import { Dynamic } from "solid-js/web";
import withErrorBoundary from "../../../utils/withErrorBoundary";
import Text from "../../../components/text/Text";
import EmptyState from "../../../components/emptyState/EmptyState";
import { getDynamicValueIcon } from "../../../utils/menu/optionIconUtil";
import { SingleSelect } from "../../../components/menu/Select";
import { useModalManager } from "../../../components/modal/ModalManager";
import { keyFromSelector } from "i18next";
import PillSingleSelectTrigger from "../../../components/menu/triggers/PillSingleSelectTrigger";

type SortType = "ALPHABETICALLY" | "ALPHABETICALLY_REVERSED" | "MOST_USED" | "LEAST_USED";
const sortOptions: { type: SortType; label: TranslationKey; compare: (a: Subform, b: Subform) => number }[] = [
  {
    type: "ALPHABETICALLY",
    label: keyFromSelector(($) => $.SUBFORMS_SORT_OPTIONS_ALPHABETICALLY),
    compare: (a, b) => a.meta.name.localeCompare(b.meta.name),
  },
  {
    type: "ALPHABETICALLY_REVERSED",
    label: keyFromSelector(($) => $.SUBFORMS_SORT_OPTIONS_ALPHABETICALLY_REVERSED),
    compare: (a, b) => b.meta.name.localeCompare(a.meta.name),
  },
  {
    type: "MOST_USED",
    label: keyFromSelector(($) => $.SUBFORMS_SORT_OPTIONS_MOST_USED),
    compare: (a, b) => Math.sign(SubformService.usedBy(b.uid).length - SubformService.usedBy(a.uid).length),
  },
  {
    type: "LEAST_USED",
    label: keyFromSelector(($) => $.SUBFORMS_SORT_OPTIONS_LEAST_USED),
    compare: (a, b) => Math.sign(SubformService.usedBy(a.uid).length - SubformService.usedBy(b.uid).length),
  },
];

const SubformsContent = () => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const [searchQuery, setSearchQuery] = createSignal("");
  const [sort, setSort] = createSignal<SortType>("ALPHABETICALLY");

  const filtered = createMemo(() =>
    SubformService.getSubforms().filter((subform) => softContains(subform.meta.name, searchQuery())),
  );

  const openSubformCreateModal = () =>
    open({
      skipClosingAnimation: true,
      content: () => (
        <SubformCreateModal
          onCreate={(subform) => {
            new SubformShowEvent(subform.uid).send();
            setSearchQuery("");
          }}
        />
      ),
    });

  return (
    <section class="isolate h-full">
      <header
        class="sticky top-0 z-10 -mb-13 -translate-y-15 border-b border-subtle bg-surface-extra-subtle pt-15 pb-3"
        data-testid="subforms-header"
      >
        <div class="mb-3 flex gap-3">
          <h2 class="text-header-xl text-primary">{t(($) => $.SUBFORMS_TITLE)}</h2>
          <IconButton
            icon="plus"
            aria-label={t(($) => $.SUBFORMS_CREATE_BUTTON)}
            variant="light"
            onClick={() => openSubformCreateModal()}
            size="medium"
          />
        </div>

        <div class="flex gap-4">
          <div class="grow">
            <SearchBar
              query={searchQuery()}
              onQueryChange={(value) => {
                setSearchQuery(value);
              }}
              placeholder={t(($) => $.SUBFORMS_SEARCH_PLACEHOLDER)}
            />
          </div>

          <div class="flex items-center gap-1">
            <span class="text-base-regular whitespace-nowrap text-secondary">{t(($) => $.SUBFORMS_SORT_LABEL)}</span>
            <div class="w-full">
              <SingleSelect<SortType>
                ariaLabel={t(($) => $.SUBFORMS_SORT_LABEL)}
                value={sort()}
                options={sortOptions.map((option) => ({
                  variant: "text",
                  value: option.type,
                  label: t(option.label),
                }))}
                onSelect={(value) => setSort(value)}
                sameWidth={false}
                hasError={false}
              />
            </div>
          </div>
        </div>
      </header>
      <article aria-label={t(($) => $.SUBFORMS_LIST_ARIA_LABEL)} class="flex-1">
        <Show
          when={!isEmpty(SubformService.getSubforms())}
          fallback={
            <EmptyState
              title={t(($) => $.SUBFORMS_NONE_EMPTY_STATE_TITLE)}
              description={t(($) => $.SUBFORMS_NONE_EMPTY_STATE_DESCRIPTION)}
              action={{
                variant: "light",
                icon: "plus",
                text: t(($) => $.SUBFORMS_CREATE_BUTTON),
                onClick: () => openSubformCreateModal(),
              }}
            />
          }
        >
          <div class="flex h-full flex-col gap-2 pb-15">
            <For
              each={filtered().sort(keyBy(sortOptions, "type")[sort()].compare)}
              fallback={
                <EmptyState
                  title={t(($) => $.SUBFORMS_FILTERED_EMPTY_STATE_TITLE)}
                  description={t(($) => $.SUBFORMS_FILTERED_EMPTY_STATE_DESCRIPTION)}
                />
              }
            >
              {(subform) => (
                <SubformListItem
                  subform={subform}
                  onDelete={({ usedBy }) => {
                    open({
                      content: () => (
                        <DestructiveModal
                          title={t(($) => $.SUBFORMS_DELETE_MODAL_TITLE)}
                          description={
                            isEmpty(usedBy)
                              ? t(($) => $.SUBFORMS_DELETE_MODAL_DESCRIPTION_NOT_IN_USE)
                              : t(($) => $.SUBFORMS_DELETE_MODAL_DESCRIPTION_IN_USE)
                          }
                          inlineMessage={
                            isEmpty(usedBy) ? (
                              NothingToRender
                            ) : (
                              <ul class="ml-6 list-outside list-disc">
                                <For each={usedBy}>{(field) => <li>{field.properties.label_text}</li>}</For>
                              </ul>
                            )
                          }
                          confirmLabel={t(($) => $.DELETE_BUTTON)}
                          cancelLabel={t(($) => $.CANCEL_BUTTON)}
                          onConfirm={() => {
                            SubformService.deleteSubform(subform.uid);
                          }}
                        />
                      ),
                    });
                  }}
                />
              )}
            </For>
          </div>
        </Show>
      </article>
    </section>
  );
};

type SubformListItemProps = {
  subform: Subform;
  onDelete: (subform: Subform & { usedBy: FieldConfig<SubformFieldProperties | PinFieldProperties>[] }) => void;
};
const SubformListItem = (props: SubformListItemProps) => {
  const { t } = useTranslation();
  const [editingName, setEditingName] = createSignal(false);
  const hasErrors = createMemo(() => SubformService.hasErrorsInSubform(props.subform.uid));
  const usedBy = createMemo(() => uniq(SubformService.usedBy(props.subform.uid)));
  const usedByLabel = createMemo(() =>
    isEmpty(usedBy()) ? t(($) => $.USED_BY_NONE) : t(($) => $.USED_BY, { count: usedBy().length }),
  );

  return (
    <article
      aria-label={props.subform.meta.name}
      class="relative flex items-center rounded-sm border bg-surface-default"
      classList={{
        "border-subtle": !hasErrors(),
        "border-error": hasErrors(),
      }}
    >
      <Dynamic
        component={editingName() ? "div" : "button"}
        class="flex h-15.5 w-full items-center gap-1.5 rounded-sm p-4 focus-visible:outline-default"
        classList={{ "hover:bg-surface-hover": !editingName() }}
        {...(!editingName() ? { onClick: () => new SubformShowEvent(props.subform.uid).send() } : {})}
      >
        <Icon name="documentDuplicate" type="solid" size="xl" class="shrink-0 icon-primary" />
        <div class="max-w-116.75 text-medium-semibold text-primary">
          <InlineEditor
            maxLength={MAX_NUMBER_SM}
            value={props.subform.meta.name}
            setValue={(value) => {
              SubformService.updateSubformName(props.subform.uid, value);
            }}
            label={t(($) => $.SUBFORMS_EDIT_FORM_NAME)}
            open={editingName()}
            setOpen={setEditingName}
            displayView={() => <Text as="h3" text={props.subform.meta.name} truncate />}
          />
        </div>
      </Dynamic>
      <div class="absolute right-4 flex items-center gap-3">
        <MenuPill
          label={usedByLabel()}
          icon="link"
          Trigger={(arkProps) => (
            <PillSingleSelectTrigger
              arkProps={arkProps()}
              value={{
                variant: "icon",
                icon: { name: "link", type: "solid" },
                label: usedByLabel(),
                value: props.subform.uid,
              }}
              disabled={isEmpty(usedBy())}
            />
          )}
          onSelect={(fieldId) => NavigationService.navigateToField(fieldId, "instant")}
          items={usedBy().map((field) => {
            const icon = getDynamicValueIcon(field.uid, field.type);
            return {
              variant: "dynamicValue",
              value: field.uid,
              label: field.properties.label_text,
              pill: field.properties.data_name,
              icon,
            };
          })}
        />
        <IconButtonMenu
          aria-label={t(($) => $.ACTIONS_ARIA)}
          items={[
            [
              {
                value: "rename",
                type: "action",
                onClick: () => setEditingName(true),
                variant: "text",
                label: t(($) => $.SUBFORMS_ACTIONS_RENAME),
              },
              {
                value: "duplicate",
                type: "action",
                onClick: () => SubformService.duplicateSubform(props.subform.uid),
                variant: "text",
                label: t(($) => $.SUBFORMS_ACTIONS_DUPLICATE),
              },
              {
                value: "delete",
                type: "action",
                onClick: () => props.onDelete({ ...props.subform, usedBy: usedBy() }),
                variant: "text",
                label: t(($) => $.SUBFORMS_ACTIONS_DELETE),
              },
            ],
          ]}
        />
      </div>
    </article>
  );
};

const SubformsContentWithErrorBoundary = withErrorBoundary(SubformsContent);

export default SubformsContentWithErrorBoundary;
